/**
 *
 * INFINITE SCROLL CUSTOM ELEMENT
 * Handles infinite scroll of child elements inside the x-scroller element.
 *
 */

class ScrollerElement extends HTMLElement {
  scrollerInner = null
  static ANIMATED_ATTRIBUTE = 'data-animated'
  static INNER_SELECTOR = '.scroller__inner'
  prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  isDesktop = window.matchMedia('(min-width: 768px)').matches

  // Configuration (Defaults)
  pauseDuration = 3000 // ms
  transitionDuration = 500 // ms - Should match CSS
  dtAnimationDuration = 10000 // ms
  mbAnimationDuration = 3000 // ms

  // State
  currentIndex = 0
  itemHeight = 0 // Assuming uniform height for vertical animation
  totalItems = 0
  animationInterval = null
  transitionEndListener = null
  mouseEnterListener = null
  mouseLeaveListener = null
  originalItems = [] // Store only the original nodes
  resizeListener = null

  connectedCallback() {
    if (this.prefersReducedMotion) return
    this.setupScroller()
    this.resizeListener = this.handleResize.bind(this)
    window.addEventListener('resize', this.resizeListener)
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.resizeListener)
    this.stopVerticalAnimation() // Clear intervals and listeners
  }

  setupScroller() {
    this.stopVerticalAnimation() // Stop any previous animation
    this.scrollerInner = this.querySelector(ScrollerElement.INNER_SELECTOR)
    if (!this.scrollerInner) return

    // Store/restore original items if needed
    this.restoreOriginalItems()

    // Get config from attributes
    this.pauseDuration = parseInt(this.dataset.pauseDuration || this.pauseDuration, 10)
    this.dtAnimationDuration = parseInt(this.dataset.dtAnimationDuration || this.dtAnimationDuration, 10)
    this.mbAnimationDuration = parseInt(this.dataset.mbAnimationDuration || this.mbAnimationDuration, 10)
    this.scrollerInner.style.transitionDuration = `${this.mbAnimationDuration}ms;`
    this.scrollerInner.style.setProperty('--_dt-animation-duration', `${this.dtAnimationDuration}ms`)
    this.scrollerInner.style.setProperty('--_mb-animation-duration', `${this.mbAnimationDuration}ms`)

    this.originalItems = Array.from(this.scrollerInner.children)
    this.totalItems = this.originalItems.length

    if (this.totalItems <= 1) return // No need to animate

    if (this.isDesktop) {
      this.setupDesktopAnimation()
    } else {
      this.setupMobileAnimation()
    }
  }

  restoreOriginalItems() {
    if (!this.scrollerInner || this.originalItems.length === 0) return

    // Clear any existing transition listeners first
    if (this.transitionEndListener) {
      this.scrollerInner.removeEventListener('transitionend', this.transitionEndListener)
      this.transitionEndListener = null
    }

    // If children count differs, assume duplicates exist and remove them
    while (this.scrollerInner.children.length > this.originalItems.length) {
      this.scrollerInner.removeChild(this.scrollerInner.lastChild)
    }

    // Reset transform just in case
    this.scrollerInner.style.transform = ''
  }

  setupDesktopAnimation() {
    this.removeAttribute(ScrollerElement.ANIMATED_ATTRIBUTE) // Reset just in case
    this.restoreOriginalItems()

    // Duplicate items for infinite effect
    if (this.totalItems > 0 && this.scrollerInner.children.length === this.totalItems) {
      this.originalItems.forEach((item) => {
        const duplicate = item.cloneNode(true)
        duplicate.setAttribute('aria-hidden', 'true')
        this.scrollerInner.appendChild(duplicate)
      })
    }
    this.setAttribute(ScrollerElement.ANIMATED_ATTRIBUTE, 'true')
  }

  setupMobileAnimation() {
    this.removeAttribute(ScrollerElement.ANIMATED_ATTRIBUTE)
    this.restoreOriginalItems() // Ensures we start clean and remove potential desktop duplicates

    if (this.totalItems <= 1) return

    // Duplicate items for infinite effect *after* calculating height
    this.itemHeight = this.originalItems[0]?.offsetHeight || 0
    if (this.itemHeight === 0) {
      console.warn('Scroller: Could not determine item height for vertical animation.')
      return
    }

    // Duplicate items if not already done
    if (this.totalItems > 0 && this.scrollerInner.children.length === this.totalItems) {
      this.originalItems.forEach((item) => {
        const duplicate = item.cloneNode(true)
        duplicate.setAttribute('aria-hidden', 'true') // Keep accessibility correct
        this.scrollerInner.appendChild(duplicate)
      })
    }

    this.currentIndex = 0
    this.applyVerticalTransform() // Set initial position before starting animation
    this.startVerticalAnimation()

    // Add hover listeners for pause/resume
    this.mouseEnterListener = this.stopVerticalAnimation.bind(this)
    this.mouseLeaveListener = this.startVerticalAnimation.bind(this)
    this.scrollerInner.addEventListener('mouseenter', this.mouseEnterListener)
    this.scrollerInner.addEventListener('mouseleave', this.mouseLeaveListener)
  }

  startVerticalAnimation() {
    this.stopVerticalAnimation() // Clear existing interval and listeners

    // Define the listener function
    this.transitionEndListener = () => {
      // This listener executes *after* the transition to the first duplicate item completes
      if (this.currentIndex === this.totalItems) {
        this.scrollerInner.style.transition = 'none' // Disable transition for the jump
        this.currentIndex = 0 // Reset index to the start
        this.applyVerticalTransform() // Jump to the initial position visually
        // Force reflow/repaint to ensure the transform applies instantly
        // Reading offsetHeight is a common trick to trigger reflow
        // eslint-disable-next-line no-unused-expressions
        this.scrollerInner.offsetHeight
        // Re-enable transition for subsequent animations
        // It will be properly set in the next interval callback
        // Setting it here might cause a flash if the interval fires immediately
      }
    }

    // Attach the listener
    this.scrollerInner.addEventListener('transitionend', this.transitionEndListener)

    this.animationInterval = setInterval(() => {
      this.currentIndex++ // Increment index to move to the next item (or first duplicate)

      // Ensure transition is set *before* changing the transform for the animation step
      this.scrollerInner.style.transition = `transform ${this.mbAnimationDuration}ms ease-in-out`
      this.applyVerticalTransform()

      // The transitionEndListener will handle the reset when currentIndex reaches totalItems
    }, this.pauseDuration + this.mbAnimationDuration)

    // Remove hover listeners if they exist
    if (this.mouseEnterListener && this.scrollerInner) {
      this.scrollerInner.removeEventListener('mouseenter', this.mouseEnterListener)
      this.mouseEnterListener = null
    }
    if (this.mouseLeaveListener && this.scrollerInner) {
      this.scrollerInner.removeEventListener('mouseleave', this.mouseLeaveListener)
      this.mouseLeaveListener = null
    }
  }

  stopVerticalAnimation() {
    if (this.animationInterval) {
      clearInterval(this.animationInterval)
      this.animationInterval = null
    }
    // Remove the transition listener if it exists
    if (this.transitionEndListener && this.scrollerInner) {
      this.scrollerInner.removeEventListener('transitionend', this.transitionEndListener)
      this.transitionEndListener = null
    }
  }

  applyVerticalTransform() {
    const offset = this.currentIndex * this.itemHeight
    this.scrollerInner.style.transform = `translateY(-${offset}px)`
  }

  handleResize() {
    const currentlyDesktop = window.matchMedia('(min-width: 768px)').matches
    if (currentlyDesktop !== this.isDesktop) {
      this.isDesktop = currentlyDesktop
      this.setupScroller() // Re-initialize based on new size
    }
  }
}

customElements.define('x-scroller', ScrollerElement)
